import { LocalTrackEvent, MULTI_VIDEO_DATA_TYPE, MonitorEventId, RecaptureMode, VideoContentHint, VideoPlayer, addMonitorEvent } from '..';
import { DOC_BILLING_CN, DOC_BILLING_OVERSEA, NAME, defaultBigVideoProfile } from '../common/constants';
import { DeviceChangeEvent, DeviceInfo, deviceDetector, getCameras } from '../common/device-detector';
import { IS_GET_CAPABILITIES_FROM_INPUTDEVICE_SUPPORTED, IS_GET_SETTINGS_SUPPORTED } from '../common/rtc-detection';
import { CaptureOption, FacingMode, MediaType, PlayOption, TrackEvent } from '../enums';
import innerEmitter from '../inner-emitter/inner-emitter';
import INNER_EVENT from '../inner-emitter/inner-event';
import { VideoManager } from '../manager/video-manager';
import { IOS_MAIN_VERSION, IOS_VERSION, IS_ANDROID, IS_CHROMIUM_BASE, IS_FIREFOX, IS_HUAWEI, IS_IOS_13_OR_14, IS_OPENHARMONY, isMobile, isVersionLargerThan, isVersionSmallerThan } from '../utils/environment';
import { calculateScaleResolutionDownNumber, isOverseaSdkAppId, isString, isUndefined, isVideoMixerOutputTrack } from '../utils/utils';
import { SEIEncoder } from '../utils/sei-encoder';
import { LocalTrack, LocalTrackState } from './local-track';
import { after } from '../utils/decorators/middleware';

export class LocalVideoTrack extends LocalTrack<'video'> {
  profile: VideoProfile = {
    ...defaultBigVideoProfile
  };
  avoidCropping = false;
  private _scaleResolutionDownBy?: number;
  stat: LocalVideoStat = {
    bytesSent: 0,
    packetsSent: 0,
    framesEncoded: 0,
    framesSent: 0,
    frameWidth: 0,
    frameHeight: 0,
    fpsCapture: 0,
    framesCaptured: 0
  };
  small?: VideoProfile;
  isNeedToSetBandwidth?: boolean;
  muteImage?: string;
  manager?: VideoManager;
  _seiCodec = new SEIEncoder();
  get facingMode(): FacingMode | undefined {
    if (!IS_GET_SETTINGS_SUPPORTED || !this.mediaTrack) return;
    return this.mediaTrack.getSettings().facingMode as FacingMode;
  }
  get contentHint(): VideoContentHint {
    return (this._inputTrack?.contentHint || this.outMediaTrack?.contentHint || '') as VideoContentHint;
  }
  get isQosClearFirst() {
    return this._inputTrack?.contentHint === 'detail';
  }

  get hasSmall() {
    return !!this.manager?.hasSmall;
  }
  constructor(vm?: VideoManager, mediaType = MediaType.BIG_VIDEO) {
    super(mediaType, VideoPlayer, vm?.room);
    this.manager = vm;
    const check2k4k = () => {
      if (!this.isAllowed2k4k(this.profile)) {
        const docLink = isOverseaSdkAppId(this.room?.sdkAppId || 0) ? DOC_BILLING_OVERSEA : DOC_BILLING_CN;
        this.log.warn(`Resolution is reset to 1080p, need to upgrade ability here ${docLink}`);
        this.setProfile({
          ...this.profile,
          width: 1920,
          height: 1080
        });
        this.applyProfile();
      } else {
        if (this.room && this.settings.height >= 1440 && this.state === LocalTrackState.PUBLISH) {
          this.room.sendAbilityStatus({ '2k4k': 1 });
        }
      }
    };
    this.on(TrackEvent.INPUT_MEDIA_TRACK_CHANGED, check2k4k);
    this.on(LocalTrackState.PUBLISH, check2k4k);

    // 避免多实例没有正常注册事件监听
    this.handleCameraAdded = this.handleCameraAdded.bind(this);
    this.handleCameraRemoved = this.handleCameraRemoved.bind(this);
  }

  async setMute(mute: boolean | string) {
    if (isString(mute)) {
      if (this.muteImage === mute) {
        return;
      }
      await this.manager?.deleteWatermark('mute');
      await this.manager?.setWatermark({ x: 0, y: 0, width: this.settings.width, height: this.settings.height, type: 'mute', zIndex: 999, imageUrl: mute, fillVideo: true });
      this.muteImage = mute;
      super.setMute(false);
    } else {
      if (this.muteImage) {
        await this.manager?.deleteWatermark('mute');
        this.muteImage = undefined;
      }
      super.setMute(mute);
    }
  }

  @after(function (this: LocalVideoTrack, option: CaptureOption) {
    // 主流默认流畅度优先、辅流默认清晰度优先
    this.setContentHint(option.contentHint || VideoContentHint.MOTION);
  })
  async capture({ deviceId, facingMode, useExactDeviceId = true, customSource, retryWhenExactFailed = true }: CaptureOption) {
    const option = {
      audio: false,
      video: true,
      facingMode: facingMode || this.facingMode,
      cameraId: deviceId,
      width: this.profile.width,
      height: this.profile.height,
      frameRate: this.profile.frameRate,
      useExactDeviceId,
      retryWhenExactFailed,
      customSource
    };
    if (option.facingMode === FacingMode.environment) {
      const result = await this.getDeviceIdWhenUsingBackCamera();
      if (result) option.cameraId = result;
    }
    return super.capture(option);
  }

  setProfile(videoProfile: VideoProfile) {
    const profile = this.fallbackProfile(videoProfile);
    // 标记是否需要设置上行的 bandwidth，在 applyProfile 成功后会检查
    if (profile.bitrate) this.isNeedToSetBandwidth = profile.bitrate !== this.profile.bitrate;
    if (this.isAllowed2k4k(this.profile)) super.setProfile(profile);
    else {
      const docLink = isOverseaSdkAppId(this.room?.sdkAppId || 0) ? DOC_BILLING_OVERSEA : DOC_BILLING_CN;
      this.log.warn(`Resolution is reset to 1080p, need to upgrade ability here ${docLink}`);
      super.setProfile({
        ...this.profile,
        width: 1920,
        height: 1080
      });
    }
  }
  async applyProfile() {
    if (!this.mediaTrack) return;
    this.log.info('applyProfile', this.profile);
    const { width = 0, height = 0 } = (this.sourceTrack || this.mediaTrack).getSettings();
    const prevArea = width * height;
    const trackSettings = this.settings;
    const isNeedToApplyConstraints = trackSettings.height !== this.profile.height ||
      trackSettings.width !== this.profile.width ||
      trackSettings.frameRate !== this.profile.frameRate;
    if (isNeedToApplyConstraints) {
      if (IOS_MAIN_VERSION === 16 && this.deviceId) {
        await this.recapture(this.deviceId); //  16 版本 iOS 上，改变分辨率后需要重新 capture 一次，否则可能竖屏分辨率变横屏，Canvas 用横屏分辨率渲染了竖屏分辨率
      } else {
        await (this.sourceTrack || this.mediaTrack)?.applyConstraints({
          width: this.profile.width,
          height: this.profile.height,
          frameRate: this.profile.frameRate
        });
        if (this.manager) this.manager.changeInput(this);
      }
      if (this.room && this.settings.height >= 1440 && this.state === LocalTrackState.PUBLISH) {
        this.room.sendAbilityStatus({ '2k4k': 1 });
      }
    }
    if (this.isNeedToSetBandwidth && this.room && this.room.setBandWidth) {
      this.isNeedToSetBandwidth = false;
      const { width = 0, height = 0 } = (this.sourceTrack || this.mediaTrack).getSettings();
      const currentArea = width * height;
      if (isNeedToApplyConstraints && currentArea && prevArea && currentArea === prevArea) {
        this.log.warn('set bandwidth failed: resolution is not changed');
        return;
      }
      return this.room.setBandWidth({ bandwidth: this.profile.bitrate, type: NAME.VIDEO, videoType: NAME.BIG });
    }
  }
  get settings(): { width: number; height: number; frameRate: number; } {
    const result = {
      width: this.profile.width,
      height: this.profile.height,
      frameRate: this.profile.frameRate
    };
    const track = this.sourceTrack || this.mediaTrack;
    // Chrome 59 以下不支持 getSettings，此处需要判断支持度
    if (IS_GET_SETTINGS_SUPPORTED && track) {
      // firefox 上 getSettings() 可能得到的结果是 {}，所以要判断一下有无 width & height
      Object.assign(result, track.getSettings());
    }
    return result;
  }

  get scaleResolutionDownBy() {
    if (this._scaleResolutionDownBy) return this._scaleResolutionDownBy;
    return calculateScaleResolutionDownNumber(this.settings, this.profile);
  }
  isAllowed2k4k(profile: VideoProfile) {
    // 进房之前，不做限制
    if (!this.room || !this.room.scheduleResult) return true;
    // 是屏幕分享，不做限制
    if (this.isScreen) return true;
    // 小于 2k，不做限制
    if (profile.height * profile.width < 2560 * 1440) return true;
    // 返回下发的 2k4k 功能位
    return this.room.scheduleResult.trtcAutoConf?.['2k4k'] === 1;
  }

  isNeedToSwitchDevice(newDeviceId: string) {
    // !this.mediaTrack 时要 return 的原因：licode 保护逻辑，禁止在没有 track 时 switch
    if (!this.mediaTrack || this.deviceId === newDeviceId || this.facingMode === newDeviceId) return false;
    return true;
  }

  async switchDevice(deviceId: string | FacingMode) {
    try {
      if (!this.isNeedToSwitchDevice(deviceId) && !this.isUseCustomSource) return;
      const captureOption: CaptureOption = { useExactDeviceId: true, retryWhenExactFailed: false };
      if (deviceId === FacingMode.user || deviceId === FacingMode.environment) {
        captureOption.facingMode = deviceId;
      } else {
        captureOption.deviceId = deviceId;
      }
      // 自定义采集的 track 不需要 stop
      if (this.sourceTrack) {
        this.sourceTrack.stop();
      }
      await this.capture(captureOption);
      innerEmitter.emit(INNER_EVENT.SWITCH_DEVICE_SUCCESS, { track: this });
      this.log.info('switch camera success');
    } catch (error) {
      this.log.error(`switch camera failed ${error}`);
      if (this.deviceId) {
        this.recapture(this.deviceId);
      }
      throw error;
    }
  }

  async getDeviceIdWhenUsingBackCamera() {
    let deviceId;
    try {
      // 实测纯血鸿蒙不适用这个策略，因此忽略纯血鸿蒙。
      if (IS_HUAWEI && !IS_OPENHARMONY && IS_GET_CAPABILITIES_FROM_INPUTDEVICE_SUPPORTED) {
        const cameraList = await getCameras(true);
        const backCameraList = cameraList.map(camera => ({ ...camera, capabilities: camera.getCapabilities?.() })).filter(camera => camera.capabilities?.facingMode?.includes(FacingMode.environment));
        let maxResolutionCamera = backCameraList[0];
        backCameraList.forEach(camera => {
          const { capabilities } = camera;
          const currentMaxResolution = capabilities.width?.max && capabilities.height?.max ? capabilities.width.max * capabilities.height.max : 0;
          const maxResolution = maxResolutionCamera.capabilities.width?.max && maxResolutionCamera.capabilities.height?.max ? maxResolutionCamera.capabilities.width.max * maxResolutionCamera.capabilities.height.max : 0;
          if (currentMaxResolution > maxResolution) maxResolutionCamera = camera;
        });
        if (maxResolutionCamera?.capabilities) {
          this._log.info('use max resolution back camera', maxResolutionCamera);
          deviceId = maxResolutionCamera.deviceId;
        }
      }
    } catch (error) {
      this._log.warn('get max res camera failed', error);
    }
    return deviceId;
  }

  async updateSmallConfig(profile: VideoProfile) {
    this._log.info(`update small stream config: ${JSON.stringify(profile)}`);
    const needEnableSmall = !this.small;
    this.small = this.fallbackProfile(profile, true);
    await this.manager?.update();
    if (needEnableSmall) await this.room?.enableSmall(true);
    this.log.info('update small stream config success');
  }

  fallbackProfile(profile: VideoProfile, isSmall = false): VideoProfile {
    const IS_WIDTH_BIGGER_THAN_HEIGHT = profile.width > profile.height;
    const result = { ...profile };
    if (profile.width * profile.height <= 160 * 120 && IS_ANDROID && IS_CHROMIUM_BASE) {
      this.log.warn(`${isSmall ? 'small ' : ''}resolution is ${profile.width}*${profile.height}, fallback to 240*180 for android chrome`);
      result.width = IS_WIDTH_BIGGER_THAN_HEIGHT ? 240 : 180,
      result.height = IS_WIDTH_BIGGER_THAN_HEIGHT ? 180 : 240,
      result.bitrate = Math.max(profile.bitrate, 150);
    }
    // 在 iOS 13 & 14 h264 无法正常编码大于 720p 的视频，此处 fallback 到 720p，保障正常通话
    // ref: http://tapd.oa.com/TerWebG/prong/stories/view/1020396022872394289
    if (profile.width * profile.height > 1280 * 720 && IS_IOS_13_OR_14) {
      result.width = IS_WIDTH_BIGGER_THAN_HEIGHT ? 1280 : 720;
      result.height = IS_WIDTH_BIGGER_THAN_HEIGHT ? 720 : 1280;
      this.log.warn('reset to 1280 * 720 on iOS 13~14');
    }
    // IOS (14.0 ~ 14.2) 采集 720p / 480p 时, 会触发 video track end. 测试 360p 正常.
    // ref: https://tapd.woa.com/tapd_fe/20396022/story/detail/1020396022125441067
    if (isVersionSmallerThan(IOS_VERSION, '14.3') && isVersionLargerThan(IOS_VERSION, '14.0', true)) {
      this.on(LocalTrackEvent.TRACK_ENDED, () => {
        const IS_WIDTH_BIGGER_THAN_HEIGHT = this.profile.width > this.profile.height;
        if (this.profile.width * this.profile.height > (640 * 480)) {
          this.profile.width = IS_WIDTH_BIGGER_THAN_HEIGHT ? 640 : 480;
          this.profile.height = IS_WIDTH_BIGGER_THAN_HEIGHT ? 480 : 640;
          this.log.warn('reduce the resolution to 480p on iOS 14.0 ~ 14.2');
        } else if (this.profile.width * this.profile.height > (640 * 360)) {
          this.profile.width = IS_WIDTH_BIGGER_THAN_HEIGHT ? 640 : 360;
          this.profile.height = IS_WIDTH_BIGGER_THAN_HEIGHT ? 360 : 640;
          this.log.warn('reduce the resolution to 360p on iOS 14.0 ~ 14.2');
        }
      });
    }

    // avoidCropping 给客户控制开关。
    // 针对 PC Chrome 低于 720p 的 16:9 分辨率，例如 360p，使用 720p 采集，在编码时缩放到对应分辨率。
    if (!isSmall && this.avoidCropping && (IS_CHROMIUM_BASE || IS_FIREFOX) && (!isMobile()) && profile.width * profile.height <= 640 * 360 && profile.width / profile.height === 16 / 9) {
      this._scaleResolutionDownBy = 1280 / profile.width;
      result.width = 1280;
      result.height = 720;
      this.log.warn(`capture 720p, scale: ${this._scaleResolutionDownBy}`);
    }
    return result;
  }

  stopSmall() {
    if (!this.small) return;
    delete this.small;
    this.manager?.update();
    this.room?.enableSmall(false);
  }

  listenDeviceChange() {
    if (deviceDetector && !deviceDetector.listeners(DeviceChangeEvent.VIDEO_INPUT_REMOVED).includes(this.handleCameraRemoved)) {
      deviceDetector.on(DeviceChangeEvent.VIDEO_INPUT_REMOVED, this.handleCameraRemoved, this);
    }
  }

  async handleCameraRemoved(device: DeviceInfo) {
    if (device.deviceId === this.deviceId) {
      let isWaitNewDevice = this.recaptureMode === RecaptureMode.WAIT_CURRENT_DEVICE;
      this.log.warn(`RecaptureMode: ${RecaptureMode[this.recaptureMode]}. Current camera is lost: ${JSON.stringify(device)}`);
      if (this.recaptureMode === RecaptureMode.AUTO_SWITCH_NEW_DEVICE) {
        addMonitorEvent(this.userId, { eventId: MonitorEventId.DEVICE_CAPTURE, param1: 7, streamType: MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG });
        const cameras = await getCameras();
        if (cameras[0]) {
          this.recapture(cameras[0].deviceId);
        } else {
          isWaitNewDevice = true; // 设备拔出后，没可用摄像头设备，则等待插入摄像头再恢复采集。
        }
      }
      if (isWaitNewDevice) {
        deviceDetector!.on(DeviceChangeEvent.VIDEO_INPUT_ADDED, this.handleCameraAdded, this);
      }
    }
  }

  async handleCameraAdded(device: DeviceInfo) {
    if (this.recaptureMode === RecaptureMode.WAIT_CURRENT_DEVICE && device.deviceId !== this.deviceId) return;
    deviceDetector!.off(DeviceChangeEvent.VIDEO_INPUT_ADDED, this.handleCameraAdded, this);
    this.log.warn(`camera added: ${JSON.stringify(device)}`);
    this.recapture(device.deviceId);
  }
  encodeFrame(frame: RTCEncodedVideoFrame, small?: boolean) {
    if (!this.manager) return frame;
    const mediaType = small ? MediaType.SMALL_VIDEO : this.mediaType;
    return this.manager.encodePipeline.reduceRight((frame, encoder) => (encoder ? encoder({ frame, mediaType }) : frame), frame);
  }
  get enableEncodeFrame() {
    if (!this.manager) return false;
    return this.manager.encodePipeline.some(x => x);
  }
  play(container: HTMLElement | HTMLElement[] | null, options?: PlayOption) {
    if (isUndefined(this.mirror) && !this.isScreen) {
      this.setMirror('view');
    }
    return super.play(container, options);
  }
  close() {
    if (this.muteImage) {
      this.manager?.deleteWatermark('mute');
      this.muteImage = undefined;
    }
    deviceDetector!.off(DeviceChangeEvent.VIDEO_INPUT_ADDED, this.handleCameraAdded, this);
    deviceDetector!.off(DeviceChangeEvent.VIDEO_INPUT_REMOVED, this.handleCameraRemoved, this);
    super.close();
  }
  async recapture(deviceId: string): Promise<void> {
    try {
      await super.recapture(deviceId);
    } catch (error) {
      const otherCamera = (await getCameras()).find(camera => camera.deviceId !== deviceId);
      if (otherCamera) {
        await super.recapture(otherCamera.deviceId);
      } else {
        throw error;
      }
    }
  }

  setContentHint(hint: VideoContentHint) {
    if (this.mediaTrack && 'contentHint' in this.mediaTrack) {
      if (this.mediaTrack.contentHint !== hint) {
        this.log.info(`setContentHint ${hint}`);
        this.mediaTrack.contentHint = hint;
      }
    }
    // 确保 outputTrack 和 mediaTrack 的 contentHint 保持一致。
    // TIP：自定义采集没有mediaTrack，需要支持设置 outMediaTrack 的 contentHint
    if (this.outMediaTrack && this.outMediaTrack.contentHint !== hint) {
      this.outMediaTrack.contentHint = hint;
    }
  }

  setRotation(rotation?: 0 | 90 | 180 | 270) {
    if (!this.manager) return;
    if (this.isScreen) return;
    if (isUndefined(rotation)) return;
    if (rotation === this.rotation) return;
    this.rotation = rotation;
    this.manager.rotation = rotation;
  }

  setOutputMediaStreamTrack(output: MediaStreamVideoTrack): void | Promise<void> {
    const preTrack = this._outputTrack;
    // 已经推合图流，localVideoTrack 不能切换输出流和replace publish track，要保留合图流的 track。有潜在的副作用，需要在room 增加参数控制这个逻辑是否生效
    // TIP：如果没有 publish 则 this.room 为 null，有可能导致 isVideoMixerOutputTrack 判断不准确，可以通过前处理manager获取room
    if (this instanceof LocalVideoTrack && isVideoMixerOutputTrack(preTrack, this.room || this.manager?.room)) return;
    return super.setOutputMediaStreamTrack(output);
  }
}
